import { useState } from 'react';
import { s } from '@/theme/tokens';
import { Box, DialogLayout, Panel, Stack } from '@/ui';
import { settingsPanelSurfaceSx } from '@/ui/composites/settingsPageTypographySx';
import { Button } from '@/ui/primitives/Button';
import { ProjectCsvValidationPreviewResultsSection } from './ProjectCsvValidationPreviewResultsSection';
import { ProjectCsvValidationResultPanel } from './ProjectCsvValidationResultPanel';
import { ValidationPreviewOverallStatusPanel } from './ValidationPreviewOverallStatusPanel';
import type { ValidationPreviewData } from './validationDataChecks';
import type { ReactElement, ReactNode } from 'react';

type ProjectCsvUploadValidationPanelProps = {
  validationInput: ReactNode;
  clearDisabled: boolean;
  clearValidation: () => void;
  validatedFileName: string | null;
  openFilePicker: () => void;
  hasValidatedFile: boolean;
  previewData: ValidationPreviewData;
  previewHeaders: string[];
  previewBodyRows: string[][];
  normalizeHeader: (value: string) => string;
  normalizeStatus: (value: string) => string;
  resolveMatchedHeaderLabel: (header: string) => string | null;
  resolveMatchedStatusLabel: (value: string, columnIndex: number) => string | null;
};

export const ProjectCsvUploadValidationPanel = ({
  validationInput,
  clearDisabled,
  clearValidation,
  validatedFileName,
  openFilePicker,
  hasValidatedFile,
  previewData,
  previewHeaders,
  previewBodyRows,
  normalizeHeader,
  normalizeStatus,
  resolveMatchedHeaderLabel,
  resolveMatchedStatusLabel,
}: ProjectCsvUploadValidationPanelProps): ReactElement => {
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <>
      <Panel
        border={false}
        footerGap="xs"
        contentGap="sm"
        surfaceVariant="settings"
        sx={[settingsPanelSurfaceSx, { pb: s('lg') }]}
      >
        {validationInput}
        <Stack gap="md">
          <ProjectCsvValidationResultPanel
            hasValidatedFile={hasValidatedFile}
            previewData={previewData}
            fileName={validatedFileName}
            container="embedded"
            actions={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: s('md'), flexShrink: 0 }}>
                {!hasValidatedFile ? (
                  <Button variant="secondary" fullWidthOnMobile={false} onClick={openFilePicker}>
                    Validate File
                  </Button>
                ) : null}
                {hasValidatedFile ? (
                  <Button
                    variant="outlined"
                    fullWidthOnMobile={false}
                    onClick={() => {
                      setDetailsOpen(true);
                    }}
                  >
                    Details
                  </Button>
                ) : null}
                {!clearDisabled ? (
                  <Button variant="ghost" fullWidthOnMobile={false} onClick={clearValidation}>
                    Clear
                  </Button>
                ) : null}
              </Box>
            }
          />
        </Stack>
      </Panel>
      <DialogLayout
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
        }}
        title="Validation Details"
        maxWidth="md"
        scroll="body"
        headerGap="xs"
        contentOffset="xs"
        footerGap="md"
        actions={
          <Button
            variant="secondary"
            onClick={() => {
              setDetailsOpen(false);
            }}
          >
            Close
          </Button>
        }
        actionsAlign="flex-end"
      >
        <Stack gap="xl">
          <ProjectCsvValidationPreviewResultsSection
            fileName={validatedFileName}
            previewHeaders={previewHeaders}
            previewBodyRows={previewBodyRows}
            previewData={previewData}
            normalizeHeader={normalizeHeader}
            normalizeStatus={normalizeStatus}
            resolveMatchedHeaderLabel={resolveMatchedHeaderLabel}
            resolveMatchedStatusLabel={resolveMatchedStatusLabel}
          />
          <ValidationPreviewOverallStatusPanel
            overallStatus={previewData.overallStatus}
            suiteCounts={previewData.suiteCounts}
            testCounts={previewData.testCounts}
            suiteNames={previewData.suiteNames}
            suiteNamesByStatus={previewData.suiteNamesByStatus}
            testNames={previewData.testNames}
            testNamesByStatus={previewData.testNamesByStatus}
            totalSuites={previewData.totalSuites}
            totalTests={previewData.totalTests}
            totalDurationMs={previewData.totalDurationMs}
            latestTimestamp={previewData.latestTimestamp}
            container="embedded"
          />
        </Stack>
      </DialogLayout>
    </>
  );
};
